'use client';

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useContext, type ReactNode } from 'react';

/**
 * The shared package owns the *logic* for modals and sheets (the stack, the
 * navigation, the keyboard handling) but not the *presentation*. Each app supplies
 * its own shell components — built on its own design system — through
 * {@link SharedUIProvider}. That way `frontend` keeps its shadcn Dialog/Sheet and
 * `frontend-yummmsy` can render something completely different without forking
 * any of the state management.
 */

/** Props the app's modal shell receives from {@link import('./modal-renderer').ModalRenderer}. */
export interface ModalShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  className?: string;
  showCloseButton?: boolean;
  fullscreenOnMobile?: boolean;
  /** When true the shell should suppress autofocus of its first focusable child. */
  preventAutoFocus?: boolean;
  children: ReactNode;
}

/** Props the app's sheet shell receives from {@link import('./SheetManager').SheetManagerProvider}. */
export interface SheetShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Class names for width/overflow, e.g. `sm:max-w-2xl overflow-y-auto`. */
  className?: string;
  /**
   * The sheet title. The shell must render this inside its own accessible title
   * element (e.g. Radix `SheetTitle`) so the dialog is correctly labelled.
   */
  title: ReactNode;
  /** Optional prev/next navigation controls to render alongside the title. */
  headerActions?: ReactNode;
  children: ReactNode;
}
export interface SharedUIComponents {
  ModalShell: React.ComponentType<ModalShellProps>;
  SheetShell: React.ComponentType<SheetShellProps>;
}
const SharedUIContext = createContext<SharedUIComponents | null>(null);
export interface SharedUIProviderProps extends Partial<SharedUIComponents> {
  children: ReactNode;
}

/**
 * Supplies the app-specific modal and sheet shells to the shared modal/sheet system.
 * Must wrap `ModalRenderer` and `SheetManagerProvider`.
 */
export function SharedUIProvider(t0) {
  const $ = _c(6);
  const {
    children,
    ModalShell,
    SheetShell
  } = t0;
  const t1 = ModalShell ?? FallbackModalShell;
  const t2 = SheetShell ?? FallbackSheetShell;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      ModalShell: t1,
      SheetShell: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const value = t3;
  let t4;
  if ($[3] !== children || $[4] !== value) {
    t4 = <SharedUIContext.Provider value={value}>{children}</SharedUIContext.Provider>;
    $[3] = children;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
export function useSharedUI() {
  const context = useContext(SharedUIContext);
  if (!context) {
    throw new Error("Shared UI components are not registered. Wrap your app in <SharedUIProvider ModalShell={...} SheetShell={...} />.");
  }
  return context;
}

/**
 * Minimal unstyled shells so an app can mount the providers before it has wired up
 * its own design system. Not intended for production use.
 */
function FallbackModalShell(t0) {
  const $ = _c(14);
  const {
    open,
    onOpenChange,
    title,
    description,
    children
  } = t0;
  if (!open) {
    return null;
  }
  const t1 = typeof title === "string" ? title : undefined;
  let t2;
  if ($[0] !== onOpenChange) {
    t2 = () => onOpenChange(false);
    $[0] = onOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = title && <h2 className="text-lg font-semibold">{title}</h2>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== description) {
    t4 = description && <p className="text-sm text-gray-600">{description}</p>;
    $[4] = description;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t3 || $[8] !== t4) {
    t5 = <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6" onClick={_temp}>{t3}{t4}{children}</div>;
    $[6] = children;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t5) {
    t6 = <div role="dialog" aria-modal="true" aria-label={t1} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={t2}>{t5}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(event) {
  return event.stopPropagation();
}
function FallbackSheetShell(t0) {
  const $ = _c(14);
  const {
    open,
    onOpenChange,
    title,
    headerActions,
    children
  } = t0;
  if (!open) {
    return null;
  }
  const t1 = typeof title === "string" ? title : undefined;
  let t2;
  if ($[0] !== onOpenChange) {
    t2 = () => onOpenChange(false);
    $[0] = onOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== title) {
    t3 = <h2 className="flex-1 font-semibold">{title}</h2>;
    $[2] = title;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== headerActions || $[5] !== t3) {
    t4 = <div className="flex items-center gap-2 p-2">{headerActions}{t3}</div>;
    $[4] = headerActions;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children || $[8] !== t4) {
    t5 = <div className="flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white" onClick={_temp2}>{t4}{children}</div>;
    $[7] = children;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t5) {
    t6 = <div role="dialog" aria-modal="true" aria-label={t1} className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={t2}>{t5}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp2(event) {
  return event.stopPropagation();
}